/* eslint-disable no-console */
/* eslint-disable max-len */

import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import TreeView from '../../../Reusable/TreeView';
import { Box, Button, Grid, InputAdornment, Stack, TextField, Typography, CircularProgress } from '@mui/material';
import MainCard from '../../../../../../../../../UiComponents/Cards/MainCard';
import { IoSearch, IoClose } from 'react-icons/io5';
import { LanguageContext } from '../../../../../../../../../libs/Context';
import { createCategory, moveAllContent, transferContentItem, addContentToCategory, findCategoriesContaining } from '../../../../../../../../actions/resource_configuration/category_management';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Category } from '../../../../../../../../actions/resource_configuration/types';
import { useLocation, useParams } from 'react-router-dom';

// Updated interface to support both original usage and new move functionality
interface MoveToSliderProps {
	onClose: () => void;
	// Optional move functionality - if not provided, component works like original
	sourceCategoryId?: string | null;
	sourceCategoryName?: string;
	// New: Individual content move support
	contentId?: string;
	contentType?: string;
	contentName?: string;
	// Callbacks
	onMoveSuccess?: (targetCategoryId: string, targetCategoryName: string) => void;
	onMoveError?: (error: string) => void;
	// NEW: Category data from navigation context
	categoryData?: any;
}

const MoveToSlider: React.FC<MoveToSliderProps> = ({
	onClose,
	sourceCategoryId,
	sourceCategoryName,
	contentId,
	contentType,
	contentName,
	onMoveSuccess,
	onMoveError,
	categoryData
}) =>
{
	const { language } = useContext(LanguageContext);
	const routeLocation = useLocation();
	const params = useParams();
	const location = window.location.pathname;
	const [ categoryName, setCategoryName ] = useState('');
	const [ isLoading, setIsLoading ] = useState(false);
	const [ searchTerm, setSearchTerm ] = useState('');
	const [ selectedCategory, setSelectedCategory ] = useState<string | null>(null);
	const [ refreshTrigger, setRefreshTrigger ] = useState(0);

	// Move operation state
	const [ isMoving, setIsMoving ] = useState(false);
	const [ moveProgress, setMoveProgress ] = useState<'idle' | 'removing' | 'adding' | 'complete' | 'error'>('idle');

	// NEW: Internal source category resolution state - moved from ContentCard
	const [ resolvedSourceCategoryId, setResolvedSourceCategoryId ] = useState<string | null>(null);
	const [ isLookingUpSourceCategory, setIsLookingUpSourceCategory ] = useState(false);
	const [ sourceCategoryResolved, setSourceCategoryResolved ] = useState(false);

	// Get company ID from Redux store
	const session = useSelector((state: any) => state?.mysession?.session);
	const companyId = session?.companyId;

	// Check if the URL contains a category ID
	const hasCategoryId = location.includes('/categories/') && location.split('/categories/')[1]?.length > 0;
	const currentCategoryId = hasCategoryId ? location.split('/categories/')[1].split('/')[0] : null;

	// Determine move mode based on provided props
	// Priority: Individual move if contentId+contentType provided, otherwise bulk move
	const isIndividualMoveMode = Boolean(contentId && contentType);
	const isBulkMoveMode = Boolean(sourceCategoryId && sourceCategoryName && !isIndividualMoveMode);
	const isMoveMode = isBulkMoveMode || isIndividualMoveMode;

	// NEW: Enhanced source category resolution - moved from ContentCard
	const performSourceCategoryLookup = useCallback(async () =>
	{
		// Skip if we already have resolved source or no content ID
		if (sourceCategoryResolved || !contentId || !isIndividualMoveMode) return;

		// Check if we already have source from navigation context
		const navigationSources = [
			categoryData?.categoryId || categoryData?._id,
			(window.location.pathname.includes('/categories/')
				? window.location.pathname.split('/categories/')[1]?.split('/')[0]
				: ''),
			params.categoryId
		];

		const foundNavigationSource = navigationSources.find((source) => source && source.trim() !== '');

		if (foundNavigationSource && foundNavigationSource !== 'uncategorized')
		{
			console.log(`✅ Using source category from navigation for ${contentType}: ${foundNavigationSource}`);
			setResolvedSourceCategoryId(foundNavigationSource);
			setSourceCategoryResolved(true);

			return;
		}

		// Perform reverse lookup to find which category contains this content
		setIsLookingUpSourceCategory(true);

		try
		{
			const result = await findCategoriesContaining(contentId);

			if (result.categories && result.categories.length > 0)
			{
				// Use the first category found (content might be in multiple categories)
				const foundSourceCategoryId = result.categories[0]._id;

				console.log(`✅ REVERSE LOOKUP SUCCESS: Found source category ${foundSourceCategoryId} for ${contentType} ${contentId}`);
				setResolvedSourceCategoryId(foundSourceCategoryId);
			}
			else
			{
				console.warn(`⚠️ REVERSE LOOKUP: No categories found containing ${contentType} ${contentId} - content is truly uncategorized`);
				setResolvedSourceCategoryId(null);
			}
		}
		catch (error)
		{
			console.error(`❌ REVERSE LOOKUP FAILED for ${contentType} ${contentId}:`, error);
			setResolvedSourceCategoryId(null);
		}
		finally
		{
			setIsLookingUpSourceCategory(false);
			setSourceCategoryResolved(true);
		}
	}, [ contentId, contentType, categoryData, params.categoryId, sourceCategoryResolved, isIndividualMoveMode ]);

	// NEW: Source category resolution effect - only runs when MoveToSlider is opened
	useEffect(() =>
	{
		if (isIndividualMoveMode && !sourceCategoryResolved)
		{
			performSourceCategoryLookup();
		}
	}, [ isIndividualMoveMode, performSourceCategoryLookup, sourceCategoryResolved ]);

	// Determine the source category ID based on move mode and resolution
	// - Bulk moves: Always use sourceCategoryId prop (must be valid string)
	// - Individual moves: Use resolved source if available, otherwise props/URL fallback
	const currentSourceId = (() =>
	{
		if (isBulkMoveMode)
		{
			return sourceCategoryId; // For bulk moves, this should always be a valid string
		}
		if (isIndividualMoveMode)
		{
			// Use resolved source if available, otherwise fallback to props
			if (sourceCategoryResolved)
			{
				return resolvedSourceCategoryId;
			}

			// During lookup, use provided sourceCategoryId if available
			return sourceCategoryId !== undefined ? sourceCategoryId : currentCategoryId;
		}

		return currentCategoryId;
	})();

	const addCategoriesAndSubCategories = async () =>
	{
		if (!categoryName.trim())
		{
			toast.error('Category name is required');

			return;
		}

		if (!companyId)
		{
			toast.error('Company ID is missing');

			return;
		}

		try
		{
			setIsLoading(true);

			const payload = {
				name : categoryName,
				companyId,
				// If we have a category ID in the URL and we're adding a subcategory
				...(hasCategoryId && { parentCategoryId: currentCategoryId })
			};

			const result = await createCategory(payload);

			if (result && result._id)
			{
				toast.success(hasCategoryId ? 'Subcategory created successfully' : 'Category created successfully');
				setCategoryName('');

				// Refresh the categories list by incrementing the refreshTrigger
				setRefreshTrigger((prev) => prev + 1);

				// Set the newly created category as selected after a brief delay
				// to ensure the categories have been refreshed
				setTimeout(() =>
				{
					setSelectedCategory(result._id);
				}, 300);
			}
		}
		catch (error)
		{
			console.error('Error creating category:', error);
			toast.error('Failed to create category. Please try again.');
		}
		finally
		{
			setIsLoading(false);
		}
	};

	// Handle selecting a category in the TreeView
	const handleSelectCategory = (id: string | string[]) =>
	{
		// For radio selection, always get the single ID
		const categoryId = Array.isArray(id) ? id[0] : id;

		// Only update if selection actually changed
		if (categoryId)
		{
			setSelectedCategory(categoryId);
		}

		// Prevent any default browser behavior or event bubbling
		return false;
	};

	// Main move operation function - supports both bulk and individual moves
	const handleMove = async () =>
	{
		if (!isMoveMode)
		{
			// Original behavior - just close or show message
			toast.info('Please select a category');

			return;
		}

		if (!selectedCategory)
		{
			toast.error('Please select a target category');

			return;
		}

		setIsMoving(true);
		setMoveProgress('idle');

		try
		{
			setMoveProgress('removing');

			// CONDITION 1: BULK MOVE - Move all content from source category to target category
			if (isBulkMoveMode && currentSourceId)
			{
				console.log(`🔄 BULK MOVE: Moving all content from ${sourceCategoryName} (${currentSourceId}) to target category (${selectedCategory})`);

				const result = await moveAllContent(currentSourceId, selectedCategory);

				// setMoveProgress('complete');

				// // Show detailed success message based on the result
				// const { summary } = result;

				// if (summary.moved > 0)
				// {
				// 	toast.success(`${summary.moved} content items moved successfully from "${sourceCategoryName}" to target category`);
				// }
				// else if (summary.totalRequested === 0)
				// {
				// 	toast.info('No content found in the source category to move');
				// }
				// else if (summary.notFound > 0)
				// {
				// 	toast.warning('Some content was not found in the source category');
				// }
				// else if (summary.alreadyExists > 0)
				// {
				// 	toast.info('Content already exists in the target category');
				// }
				// else
				// {
				// 	toast.error('Failed to move content. Please try again.');
				// 	setMoveProgress('error');

				// 	return;
				// }

				// // Call success callback if provided
				// if (onMoveSuccess)
				// {
				// 	onMoveSuccess(selectedCategory, result.targetCategory?.name || sourceCategoryName || 'Target Category');
				// }
			}
			// CONDITION 2: INDIVIDUAL MOVE - Move single content item to target category
			else if (isIndividualMoveMode && contentId && contentType)
			{
				console.log(`🔄 INDIVIDUAL MOVE: Moving ${contentType} (${contentId}) to target category (${selectedCategory})`);

				// Use addContentToCategory API for individual content moves
				const payload = {
					contentId,
					contentType,
					title : contentName || contentType
					// Optional fields can be added if available
					// thumbnailUrl?: string,
					// duration?: number
				};

				const result = await addContentToCategory(selectedCategory, payload);

				setMoveProgress('complete');

				// Use the result to provide better feedback
				const targetCategoryName = result.name || 'target category';

				toast.success(`"${contentName || contentType}" moved successfully to "${targetCategoryName}"`);

				// Call success callback with actual target category info
				if (onMoveSuccess)
				{
					onMoveSuccess(selectedCategory, targetCategoryName);
				}
			}

			// Close the slider after successful move
			setTimeout(() =>
			{
				onClose();
			}, 1000);
		}
		catch (error)
		{
			setMoveProgress('error');

			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

			// 🔧 ENHANCED: Better error handling for specific scenarios
			let userFriendlyMessage = `Failed to move content: ${errorMessage}`;
			let debugType: 'error' | 'warn' | 'info' = 'error';

			// Handle specific API error messages
			if (errorMessage.includes('Content already exists in target category'))
			{
				userFriendlyMessage = `"${contentName || contentType}" is already in the selected category`;
				debugType = 'warn';

				// For this case, we can consider it as a "success" since the content is where the user wants it
				setTimeout(() =>
				{
					setMoveProgress('idle');
					onClose();
				}, 2000);
			}
			else if (errorMessage.includes('Source category not found'))
			{
				userFriendlyMessage = 'Source category no longer exists. Content may have been moved already.';
				debugType = 'warn';
			}
			else if (errorMessage.includes('Target category not found'))
			{
				userFriendlyMessage = 'Target category not found. Please refresh and try again.';
				debugType = 'warn';
			}
			else if (errorMessage.includes('Content not found'))
			{
				userFriendlyMessage = 'Content not found in source category. It may have been moved already.';
				debugType = 'warn';
			}
			else if (errorMessage.includes('Network Error') || errorMessage.includes('502') || errorMessage.includes('503'))
			{
				userFriendlyMessage = 'Network error. Please check your connection and try again.';
				debugType = 'error';
			}
			else if (errorMessage.includes('403') || errorMessage.includes('Forbidden'))
			{
				userFriendlyMessage = 'You do not have permission to move this content.';
				debugType = 'error';
			}

			// Show user-friendly message based on error type
			if (debugType === 'warn')
			{
				toast.warn(userFriendlyMessage);
			}
			else
			{
				toast.error(userFriendlyMessage);
			}

			// Call error callback if provided
			if (onMoveError)
			{
				onMoveError(errorMessage);
			}
		}
		finally
		{
			setIsMoving(false);
			setMoveProgress('idle');
		}
	};

	// Get move button text based on progress and mode
	const getMoveButtonText = () =>
	{
		if (!isMoveMode) return language?.save || 'Save';

		// Show resolving state during source category lookup
		if (isIndividualMoveMode && isLookingUpSourceCategory)
		{
			return 'Resolving source...';
		}

		if (!isMoving)
		{
			return isIndividualMoveMode ? (language?.moveContent || 'Move Content') : (language?.save || 'Move');
		}

		switch (moveProgress)
		{
			case 'removing':
				return isIndividualMoveMode ? 'Moving individual content...' : 'Moving all content...';
			case 'adding':
				return 'Updating hierarchy...';
			case 'complete':
				return 'Move completed!';
			case 'error':
				return 'Move failed';
			default:
				return 'Processing...';
		}
	};

	return (
		<Grid container width={'100%'} direction={'column'} flexWrap={'nowrap'} height={'100%'}
			onClick={(e) => e.stopPropagation()}
		>
			<Grid item px={2} pt={2}>
				<Stack direction='row' pb={1} alignItems={'center'} width={'100%'} display={'flex'} justifyContent={'space-between'}>
					<Typography fontSize={'22px'} color={'#353535'} fontWeight={'var(--font500)'}>{language?.movetocategory}</Typography>
					<IoClose onClick={onClose} fontSize={'22px'} cursor={'pointer'}/>
				</Stack>
				{/* Show current content info only in move mode */}
				{isMoveMode && (
					<Box mb={2} p={1} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
						{isLookingUpSourceCategory
							? (
								<Box display='flex' alignItems='center' gap={1}>
									<CircularProgress size={16} />
									<Typography variant='body2' color={'#666'}>
										Resolving source category for: {contentName || contentType}...
									</Typography>
								</Box>
							)
							: (
								<Typography variant='body2' color={'#666'}>
									{isBulkMoveMode
										? `Moving all content from: ${sourceCategoryName}`
										: `Moving: ${contentName || contentType}${currentSourceId ? ` from ${sourceCategoryName || 'current category'}` : ' (uncategorized)'}`
									}
								</Typography>
							)
						}
					</Box>
				)}
			</Grid>
			<Grid item xs minHeight={0} px={2} py={1} flex={1} sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 70px)' }}>
				<Typography variant='subtitle1' color={'#707070'} width={'100%'} >{language?.addNewCat}</Typography>
				<TextField
					fullWidth
					placeholder={language?.categoryNameTxt}
					margin='dense'
					size='small'
					value={categoryName}
					onChange={(e) => setCategoryName(e.target.value)}
					disabled={isMoveMode && isMoving}
				/>
				<Typography textAlign={'end'} color={'#707070'} width={'100%'} pb={1} fontSize={'var(--pixel12)'}>
					{categoryName.length}/50
				</Typography>
				<Button
					variant='contained'
					size='small'
					sx={{ color: 'var(--whiteColor)', p: '6px' }}
					fullWidth
					onClick={addCategoriesAndSubCategories}
					disabled={isLoading || !categoryName.trim() || (isMoveMode && isMoving)}
				>
					{isLoading ? 'Processing...' : (hasCategoryId ? (language?.addSubCat) : (language?.addcategory))}
				</Button>
				<Box width={'100%'} pt={2} pb={1}>
					<MainCard sx={{ width: '100%', maxHeight: '350px', overflowY: 'auto' }}>
						<Box position={'sticky'} top={'0'} sx={{ background: 'var(--whiteColor)', zIndex: 9 }}>
							<Typography variant='body2' color={'#333333'} pb={0.5}>{language?.selectedCategory}</Typography>
							<Box width={'100%'} py={1}>
								{/* Note: The TreeView component doesn't accept a searchTerm prop.
								This search input is kept for UI consistency but doesn't filter the TreeView content.
								A different solution would be needed to implement filtering functionality. */}
								<TextField
									fullWidth
									size='small'
									placeholder={language?.search}
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									disabled={isMoveMode && isMoving}
									sx={{
										backgroundColor            : '#F4F4F4',
										borderRadius               : '4px',
										'& .MuiOutlinedInput-root' : {
											border       : 'none',
											'& fieldset' : {
												border : 'none'
											},
											'&:hover fieldset' : {
												border : 'none'
											},
											'&.Mui-focused fieldset' : {
												border : 'none'
											}
										},
										'& .MuiInputBase-root' : {
											boxShadow : 'none'
										}
									}}
									InputProps={{
										startAdornment : (
											<InputAdornment position='start'>
												<IoSearch />
											</InputAdornment>
										)
									}}
								/>
							</Box>
						</Box>
						<Box sx={{ pointerEvents: isMoving ? 'none' : 'auto', opacity: isMoving ? 0.6 : 1 }}
							onClick={(e) => e.stopPropagation()}
						>
							<TreeView
								selectionType='radio'
								initialSelectedId={selectedCategory || undefined}
								onSelect={handleSelectCategory}
								{...(() =>
								{
									const excludeId = sourceCategoryId || currentCategoryId;

									return isMoveMode && excludeId ? { excludeCategoryId: excludeId } : {};
								})()}
								key={refreshTrigger} // Use key prop to force re-render when refreshTrigger changes
							/>
						</Box>
					</MainCard>
				</Box>
			</Grid>
			<Grid item width={'100%'} p={2}>
				<Box gap={2} display='flex' justifyContent='end'>
					<Button variant='outlined' onClick={onClose} disabled={isMoveMode && isMoving}>
						{language?.cancel}
					</Button>
					<Button
						variant='contained'
						className='whiteTxt'
						disabled={!selectedCategory ||
							(isMoveMode && (isMoving || isLookingUpSourceCategory || selectedCategory === currentSourceId))}
						{...(isMoveMode && { onClick: handleMove })}
						sx={{
							backgroundColor : isMoveMode && moveProgress === 'complete' ? '#4caf50' : undefined,
							'&:disabled'    : {
								backgroundColor : isMoveMode && moveProgress === 'error' ? '#f44336' : undefined,
								color           : isMoveMode && moveProgress === 'error' ? 'white' : undefined
							}
						}}
					>
						{getMoveButtonText()}
					</Button>
				</Box>
			</Grid>
		</Grid>
	);
};

export default MoveToSlider;